import jStat from "jstat";


export class Statistic {

    // DataTypes
    static NUMBER = "number";
    static CURRENCY = "currency";
    static PERCENT = "percent";
    // static PERCENTILE = "percentile";

    // Operations
    static AVERAGE = "average";
    static COUNT = "count";
    static PERCENTILE = "percentile"; // Also a DataType
    static SUM = "sum";
    static UNIQUE = "unique";

    // Scopes
    static UNIVERSE = "universe";
    static SAMPLE = "sample";

    constructor(operation, dataType = Statistic.NUMBER) {
        if (!operation) {
            throw new Error("You must provide an operation to OnlineStatisic. e.g. Statistic.[AVERAGE|SUM|COUNT|UNIQUE|PERCENTILE]")
        }
        this.operation = operation;
        this._sample = null;
        this._universe = null;
        this.dataType = dataType;

        if (this.operation === Statistic.AVERAGE) {
            this._sample = new Average(this.operation, dataType);
            this._universe = new Average(this.operation, dataType);
        } else if (this.operation === Statistic.COUNT) {
            this._sample = new Count(this.operation, dataType);
            this._universe = new Count(this.operation, dataType);
        } else if (this.operation === Statistic.PERCENTILE) {
            this._sample = new Population(this.operation, dataType);
            this._universe = new Population(this.operation, dataType);
        } else if (this.operation === Statistic.SUM) {
            this._sample = new Sum(this.operation, dataType);
            this._universe = new Sum(this.operation, dataType);
        } else if (this.operation === Statistic.UNIQUE) {
            this._sample = new Unique(this.operation, dataType)
            this._universe = new Unique(this.operation, dataType);
        }
    }

    static aggregate(many, dataType = Statistic.NUMBER) {
        return many.reduce((acc, other) => {
            return acc.mergeInPlace(other);
        }, new many[0].constructor(many[0].operation, dataType));
    }

    mergeInPlace(other) {
        this._sample.count += other._sample.count;
        this._sample.population = this._sample.population.concat(other._sample.population);
        this._sample.sum += other._sample.sum;
        this._sample.uniquePopulation = new Set([...this._sample.uniquePopulation, ...other._sample.uniquePopulation]);

        this._universe.count += other._universe.count;
        this._universe.population = this._universe.population.concat(other._universe.population);
        this._universe.sum += other._universe.sum;
        this._universe.uniquePopulation = new Set([...this._universe.uniquePopulation, ...other._universe.uniquePopulation]);
        return this;
    }

    add({value, isSample=true}) {
        if (isSample) {
            this._sample.count++;
            this._sample.population.push(value);
            this._sample.sum += value || 0;
            this._sample.uniquePopulation.add(value);
            if (this.operation === Statistic.PERCENTILE) {
                this._universe.count++;
                this._universe.population.push(value);
                this._universe.sum += value || 0;
                this._universe.uniquePopulation.add(value);
            }
        } else {
            this._universe.count++;
            this._universe.population.push(value);
            this._universe.sum += value || 0;
            this._universe.uniquePopulation.add(value);
        }
    }

    displayValue() {
        const value = this.value();
        let formatter = null;
        if (this.dataType === Statistic.NUMBER) {
            if (value === undefined || value === null) {
                return "N/A";
            }
            formatter = new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
            });
        } else if (this.dataType === Statistic.CURRENCY) {
            formatter = new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
        } else if (this.dataType === Statistic.PERCENT) {
            formatter = new Intl.NumberFormat('en-US', {
                style: 'percent',
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
            });
        } else if (this.dataType === Statistic.PERCENTILE) {
            // Assuming value is a percentile score, format it as a percentage
            formatter = new Intl.NumberFormat('en-US', {
                style: 'percent',
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
            })
            if (value.length === 0) {
                return {sample: "Top 0% of all products", universe: null};
            }
            const finalValue = value.reduce((acc, val) => {
                acc.sum += val.sample || 0;
                acc.count++;
                return acc;
            }, {sum: 0, count: 0});

            return {
                sample: "Top " + formatter.format(finalValue.sum / finalValue.count) + " of all products",
                universe: null
            };
        }
        value.sample = formatter.format(value.sample);
        value.universe = formatter.format(value.universe);
            // Assuming value is a percentile score, format it as a percentage

        return value;
    }

    value() {
        if (this.operation === Statistic.PERCENTILE) {
            if (this._sample.value().count === 0 || this._universe.value().count === 0) {
                return [{sample: 0, universe: 0}];
            }
            // For percentile, we need to calculate the percentile of each sample value againt the universe, otherwise
            // we only have one data point against which to run statistical tests.
            return this._sample.value().reduce((acc, val) => {
                if (val === null || val === undefined || val === 0) {
                    return acc;
                }
                const value = jStat.percentileOfScore(this._universe.value(), val);
                acc.push({sample: value, universe: value});
                return acc;
            }, []);
        }
        return {sample: this._sample.value(), universe: this._universe.value()};
    }
}

export class Aggregate {

    // Support for aggregation
    constructor(operation, dataType = Statistic.NUMBER) {
        this.sum = 0;
        this.count = 0;
        this.population = [];
        this.uniquePopulation = new Set();
        this.dataType = dataType
    }

    value() {
        return 0;
    }

    displayValue() {
        const value = this.value();
        if (isNaN(value)) {
            return null;
        }
        if (this.dataType === Statistic.NUMBER) {
            // Add commas and stuff:
            if (value === undefined || value === null) {
                return "N/A";
            }
            return new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
            }).format(value);
        } else if (this.dataType === Statistic.CURRENCY) {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(value);
        } else if (this.dataType === Statistic.PERCENT) {
            return new Intl.NumberFormat('en-US', {
                style: 'percent',
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
            }).format(value);
        } else if (this.dataType === Statistic.PERCENTILE) {
            // Assuming value is a percentile score, format it as a percentage
            return "Top " + (new Intl.NumberFormat('en-US', {
                style: 'percent',
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
            }).format(value)) + " of all products";
        }
        return value;
    }

    static aggregate(many, dataType = Statistic.NUMBER) {
        return new Aggregate(many[0].operation, dataType);
    }

    toString() {
        return JSON.stringify({
            sum: this.sum,
            count: this.count,
            population: this.population,
            uniquePopulation: Array.from(this.uniquePopulation),
            value: this.value(),
        })
    }
}


export class Average extends Aggregate {

    value() {
        return this.count === 0 ? 0 : this.sum / this.count;
    }

    valueForTTest(completePopulation) {
        return this.population;
    }
}



export class Count extends Aggregate {

    valueForTTest(completePopulation) {
        return Object.values(this.population.reduce((acc, val) => {
            if (val === null || val === undefined) {
                return acc;
            }
            if (acc[val] === undefined) {
                acc[val] = 0;
            }
            acc[val]++;
            return acc
        }, {}));
    }

    value() {
        return this.count;
    }
}


export class Population extends Aggregate {

    valueForTTest(completePopulation) {
        return [this.population.length];
    }

    value() {
        return this.population
    }
}

export class Sum extends Aggregate {

    valueForTTest(completePopulation) {
        if (this.population.length === 0) {
            return [0];
        }
        return [this.population.reduce((acc, el) => {
            return acc + (el || 0);
        }, 0)]
    }

    value() {
        return this.sum;
    }
}


export class Unique extends Aggregate {

    valueForTTest(completePopulation) {
        return [this.uniquePopulation.size];
    }

    value() {
        return this.uniquePopulation.size;
    }

}

export class SentinelAggregate {
    add(value) {
        return;
    }

    value() {
        return {sample: 0, universe: 0};
    }

    displayValue() {
        return {sample: "---", universe: "---"};
    }
}

export default Statistic;